/**
 * Working out a partner fragment's props when it doesn't declare them.
 *
 * `core/*` components ship a `{# @param … #}` manifest, so the inspector, the
 * palette entry and validation all come from the component itself. A partner's own
 * `partials/xxxl/*` predate that idea and declare nothing — but they are what makes
 * a partner's email theirs, so the editor has to offer them anyway.
 *
 * ### The one rule that does the work
 *
 * **A bare identifier is a prop; a dotted path is send-time context.**
 *
 * Read against the real partials, that is all it takes:
 *
 * | Partial | References | Inferred |
 * |---|---|---|
 * | `header` | `theme.colors.brand`, `theme.logo`, `email_config.template_vars.*` | *nothing* — a self-contained band |
 * | `appointment_overview` | `text` | `text` |
 * | `content_block` | `block_content \| raw` | `block_content` (richtext) |
 * | `footer` | `text \| raw`, `email_config.template_vars.footer \| raw` | `text` (richtext) |
 *
 * Props arrive through `{% include … with { … } %}`, which only ever passes named
 * arguments — so a dotted reference *cannot* be one, and needs no namespace
 * blacklist to exclude. Translation literals (`{{ 'webview.text'|trans }}`) are not
 * identifier references at all, so they never appear.
 *
 * An inferred manifest is a **guess**, and is marked as such all the way to the UI
 * (`BlockDefinition.inferred`): a wrong type costs one mislabelled control, and the
 * value still round-trips. Declaring a real manifest in the fragment editor is the
 * fix, and stays the recommended path.
 */
import { ComponentManifest, ManifestParam, ParamKind } from './manifest';
import { fragmentBlockType } from './registry';

/**
 * An identifier reference in an output tag: `{{ name }}`, `{{ name|raw }}`,
 * `{{ name | default('x') }}`. Deliberately **not** dotted — see the rule above —
 * and deliberately not matching quotes, so `{{ 'a.b'|trans }}` is skipped.
 */
const IDENTIFIER_OUTPUT = /\{\{-?\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*((?:\|[^}]*)?)-?\}\}/g;

/**
 * Names bound by the template itself rather than passed in: `{% for row in rows %}`
 * and `{% set total = … %}`. Reading these as props would put a loop variable in the
 * inspector.
 */
const LOCAL_BINDINGS = [
  /\{%-?\s*for\s+([a-zA-Z_][\w]*)\s*(?:,\s*([a-zA-Z_][\w]*)\s*)?in\b/g,
  /\{%-?\s*set\s+([a-zA-Z_][\w]*)\s*[=%]/g,
];

/** Compiler-injected, never a user-editable prop. */
const RESERVED = /^_cvn_/;

/**
 * Name → kind. Small and boring on purpose: the cost of a wrong guess is a
 * mislabelled control, and a longer list mostly adds ways to be confidently wrong.
 */
function inferKind(name: string, filters: string): ParamKind {
  if (/\braw\b/.test(filters)) return 'richtext';

  const lower = name.toLowerCase();
  if (/(^|_)(colour|color|bg)$/.test(lower) || lower.endsWith('_color')) return 'color';
  if (/(^|_)(url|href|link)$/.test(lower)) return 'url';
  if (/(^|_)(src|image|img|logo|icon)$/.test(lower)) return 'image';
  if (/(^|_)(width|height|size|count|padding|radius)$/.test(lower)) return 'number';
  return 'text';
}

/** Names the source binds itself, which therefore cannot be props. */
function localNames(source: string): Set<string> {
  const names = new Set<string>();
  for (const pattern of LOCAL_BINDINGS) {
    for (const match of source.matchAll(pattern)) {
      if (match[1]) names.add(match[1]);
      if (match[2]) names.add(match[2]);
    }
  }
  return names;
}

/**
 * A manifest inferred from a fragment's source.
 *
 * Always returns one — a fragment with no readable props is still placeable, it
 * just has nothing to configure (`partials/xxxl/header` is exactly that, and is
 * perfectly useful as a block).
 */
export function inferManifest(
  fragmentKey: string,
  source: string,
): ComponentManifest {
  const locals = localNames(source);
  const params: ManifestParam[] = [];
  const seen = new Set<string>();

  for (const match of source.matchAll(IDENTIFIER_OUTPUT)) {
    const name = match[1];
    const filters = match[2] ?? '';

    if (seen.has(name) || locals.has(name) || RESERVED.test(name)) continue;
    seen.add(name);

    params.push({
      name,
      kind: inferKind(name, filters),
      labelKey: null,
      required: false,
    });
  }

  return {
    type: fragmentBlockType(fragmentKey),
    labelKey: null,
    params,
    slots: [],
    inline: false,
    warnings: [],
  };
}
